import React, { createContext, useContext, ReactNode } from "react";
import { message } from "antd";

type NotificationType = "success" | "error" | "warning" | "info";

interface NotificationContextValue {
  showMessage: (
    type: NotificationType,
    content: string,
    duration?: number,
    key?: string
  ) => void;
  showPersistentMessage: (
    type: NotificationType,
    content: string,
    key?: string
  ) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
);

export const useNotification = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  // Normal Ant Design Message
  const showMessage = (
    type: NotificationType,
    content: string,
    duration: number = 3,
    key: string = "normal"
  ): void => {
    message.open({
      key,
      type,
      content: content,
      duration, // Auto-closes after set time
    });
  };

  // Persistent Message (Requires Manual Close)
  const showPersistentMessage = (
    type: NotificationType,
    content: string,
    key: string = "persistentMessage"
  ): void => {
    message.open({
      key,
      type,
      content: (
        <div>
          {content}
          <button
            style={{
              marginLeft: "10px",
              padding: "2px 5px",
              border: "none",
              backgroundColor: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              cursor: "pointer",
              borderRadius: "3px",
            }}
            onClick={() => message.destroy(key)}
          >
            Close
          </button>
        </div>
      ),
      duration: 0,
    });
  };

  return (
    <NotificationContext.Provider
      value={{ showMessage, showPersistentMessage }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
